import React, { useState, useEffect } from 'react';
import { parseLocalDate } from '../lib/dateFmt';
import { Link } from 'react-router-dom';
import { Calendar, ArrowRight, Tag, Newspaper } from 'lucide-react';
import { api } from '../lib/api';
import PageHero from '../components/PageHero';
import PageBlocks from '../components/PageBlocks';
import { useTranslation } from '../i18nContext';

const categoryColors = {
  actividad: 'bg-teal-50 text-teal-700',
  fit: 'bg-cmic-50 text-cmic-700',
  comunicado: 'bg-amber-50 text-amber-700',
  evento: 'bg-emerald-50 text-emerald-700',
};

export default function NewsPage() {
  const t = useTranslation();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    api.get('/news').then(({ data }) => setArticles(data || [])).catch(() => setArticles([])).finally(() => setLoading(false));
  }, []);

  const dateLocale = t.locale === 'en' ? 'en-US' : 'es-MX';
  const formatDate = (s) => parseLocalDate(s).toLocaleDateString(dateLocale, { year: 'numeric', month: 'long', day: 'numeric' });
  const newsBase = `/${t.locale}/${t.locale === 'en' ? 'news' : 'noticias'}`;

  const filtered = filter === 'all' ? articles : articles.filter((a) => a.category === filter);
  const categories = ['all', ...Array.from(new Set(articles.map((a) => a.category)))];

  return (
    <>
      <PageHero label={t.news.label} title={t.news.title} subtitle={t.news.subtitle} />
      <PageBlocks pageKey="news" />
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {!loading && articles.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-10" data-testid="news-filters">
              {categories.map((cat) => (
                <button key={cat} onClick={() => setFilter(cat)}
                  data-testid={`news-filter-${cat}`}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                    filter === cat ? 'bg-cmic-500 text-white' : 'bg-gray-100 text-charcoal-600 hover:bg-cmic-50 hover:text-cmic-600'}`}>
                  {cat === 'all' ? t.news.filterAll : t.news.categories[cat] || cat}
                </button>
              ))}
            </div>
          )}

          {loading ? (
            <div className="text-center py-16">
              <div className="w-10 h-10 border-3 border-cmic-200 border-t-cmic-500 rounded-full animate-spin mx-auto mb-4" />
              <p className="text-charcoal-500">{t.news.loading}</p>
            </div>
          ) : filtered.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered.map((article) => (
                <article key={article.id} data-testid={`news-card-${article.id}`} className="bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-all duration-300 border border-gray-100 hover:border-cmic-200 hover:-translate-y-0.5 flex flex-col">
                  {article.image_url && (
                    <Link to={`${newsBase}/${article.slug || article.id}`} className="aspect-video overflow-hidden bg-gray-100 block">
                      <img src={article.image_url} alt={article.title} className="w-full h-full object-cover" style={{ objectPosition: article.image_position || 'center' }} />
                    </Link>
                  )}
                  <div className="p-6 flex flex-col flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      <span className={`inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-full ${categoryColors[article.category] || 'bg-gray-100 text-gray-600'}`}>
                        <Tag size={12} />
                        {t.news.categories[article.category] || article.category}
                      </span>
                      <span className="flex items-center gap-1 text-xs text-charcoal-400">
                        <Calendar size={12} />
                        {formatDate(article.published_at)}
                      </span>
                    </div>
                    <h3 className="text-lg font-semibold text-charcoal-800 mb-2 line-clamp-2">
                      <Link to={`${newsBase}/${article.slug || article.id}`} className="hover:text-cmic-700 transition-colors">{article.title}</Link>
                    </h3>
                    <p className="text-sm text-charcoal-500 leading-relaxed mb-4 line-clamp-3 flex-1">{article.summary}</p>
                    <Link to={`${newsBase}/${article.slug || article.id}`} data-testid={`news-read-more-${article.id}`} className="inline-flex items-center gap-1 text-sm font-medium text-cmic-600 hover:text-cmic-700 transition-colors group">
                      {t.news.readMore}
                      <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                    </Link>
                  </div>
                </article>
              ))}
            </div>
          ) : (
            <div className="text-center py-16 bg-gray-50 rounded-2xl border border-gray-100" data-testid="news-empty">
              <Newspaper size={48} className="text-charcoal-300 mx-auto mb-4" />
              <p className="text-charcoal-500 text-lg mb-2">{t.news.emptyTitle}</p>
              <p className="text-charcoal-400 text-sm">{t.news.emptySubtitle}</p>
            </div>
          )}
        </div>
      </section>
    </>
  );
}
